import React from 'react';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import { toast } from 'chameleon/ui-stack/ui/toast';
import { Link } from 'react-router';
import { truncate } from 'lodash';
import InformationalPopover from 'chameleon/ui-stack/ui/popovers/informational-popover';

export const getSharedWithMeStatus = (value, isSharedWithMe, isUnShared) => {
  const isNotYetReviewed = value === 'not_yet_reviewed';
  value = isNotYetReviewed && isSharedWithMe ? 'Not yet accepted' : value;
  value = !isNotYetReviewed && isSharedWithMe ? 'Accepted' : value;
  value = value && value[0].toUpperCase() + value.slice(1);
  value = value && value.replace(/_/g, ' ');
  if (isSharedWithMe && isUnShared) {
    value = 'No longer shared';
  }
  return value;
};

export const getHeaders = (activeTab) => {
  let headers;
  switch (activeTab) {
    case 'shared_with_me':
      headers = [
        {
          Header: 'Title',
          className: `pl-4 text-base`,
          accessor: 'assessment.title',
          sortType: (a, b) =>
            (a.original.assessment || a.original).title.toLowerCase() >
            (b.original.assessment || b.original).title.toLowerCase()
              ? 1
              : -1,
        },
        {
          Header: 'Last Modified',
          className: `pl-4 text-base`,
          accessor: 'lastModified',
          isDate: true,
        },
        {
          Header: 'Date Shared',
          className: `pl-4 text-base`,
          accessor: 'sharedAt',
          isDate: true,
        },
        {
          Header: 'Shared By',
          className: `pl-4 text-base`,
          accessor: 'sharer',
        },
        {
          Header: 'Status',
          className: `pl-4 text-base`,
          accessor: 'status',
          sortType: (currentItem, nextItem) => {
            let currentItemStatus = currentItem.original.status;
            let nextItemStatus = nextItem.original.status;
            const currentItemUnshared = currentItem.original.isDisabled;
            const nextItemUnshared = nextItem.original.isDisabled;
            currentItemStatus = getSharedWithMeStatus(
              currentItemStatus,
              true,
              currentItemUnshared,
            );
            nextItemStatus = getSharedWithMeStatus(
              nextItemStatus,
              true,
              nextItemUnshared,
            );
            return currentItemStatus > nextItemStatus ? 1 : -1;
          },
        },
        {
          Header: 'MCQs',
          className: `pl-4 text-base`,
          accessor: 'itemMetadata.numberOfMcq',
        },
        {
          Header: 'FRQs',
          className: `pl-4 text-base`,
          accessor: 'itemMetadata.numberOfFrq',
        },
        {
          Header: 'Total Qs',
          className: `pl-4 text-base`,
          accessor: 'itemMetadata.numberOfQuestions',
        },
        {
          Header: (
            <span className="teacher-authored grid gap-5 grid-flow-col w-max items-center">
              Teacher-Authored
              <InformationalPopover
                withPortal
                ariaDescribedby="Teacher-Authored-popover"
                tabIndex={0}
                trigger={
                  <Icon name="info_circle_black" width="16" height="16" />
                }
                width="medium"
                ariaLabel="Teacher-authored questions popover icon"
                message="Teacher-authored questions are created by educators. They are neither authored nor reviewed by the AP Program."
              />
            </span>
          ),
          className: `pl-4 text-base`,
          accessor: 'itemMetadata.numberOfTeacherQuestions',
        },
        {
          Header: '',
          className: 'pl-4 text-base',
          accessor: '_this_wont_exist',
          showIcon: false,
        },
      ];
      break;
    case 'shared_by_me':
      headers = [
        {
          Header: 'Title',
          className: `pl-4 text-base`,
          accessor: 'assessment.title ',
          sortType: (a, b) =>
            (a.original.assessment || a.original).title.toLowerCase() >
            (b.original.assessment || b.original).title.toLowerCase()
              ? 1
              : -1,
        },
        {
          Header: 'Quiz Modified',
          className: `pl-4 text-base`,
          accessor: 'itemInfo.lastModifiedDate',
          isDate: true,
        },
        {
          Header: (
            <div className="flex">
              Share Modified &nbsp;
              <InformationalPopover
                withPortal
                tabIndex={0}
                ariaDescribedby="status-detail-tooltip"
                width="medium"
                message="Date you last shared the quiz or modified Manage Sharing settings."
                trigger={<Icon name="info_circle_black" />}
                ariaLabel="Status information icon"
              />
            </div>
          ),
          className: `pl-4 text-base`,
          accessor: 'sharedAt',
          isDate: true,
        },
        {
          Header: 'Status',
          className: `pl-4 text-base`,
          accessor: 'status',
          sortType: (a, b) =>
            a.original.assessment.is_assigned >
            b.original.assessment.is_assigned
              ? 1
              : -1,
        },
        {
          Header: 'Share Status',
          className: `pl-4 text-base`,
          accessor: 'shareStatus',
          sortType: (a, b) => {
            const valueA = a.original.isDisabled ? 'Inactive' : 'Active';
            const valueB = b.original.isDisabled ? 'Inactive' : 'Active';
            return valueA > valueB ? 1 : -1;
          },
        },
        {
          Header: 'Share Link',
          className: `pl-4 text-base`,
        },
      ];
      break;
    default:
      headers = [];
      break;
  }
  return headers;
};

export const keyToUrl = (subjectId, key) =>
  `${FYM_FRONTEND_URL}/${subjectId}/shared-quizzes/${key}`;

export const renderItemShareCopyNotification = (
  itemTitle,
  itemId,
  subjectId,
) => {
  const Message = () => (
    <div className="grid grid-flow-col items-center gap-x-24 text-base auto-cols-auto">
      <span className="w-160">
        {truncate(itemTitle, { length: 35, omission: '...' })} has been added to
        your Quizzes in All Resources. Go to Edit Quiz to assign or edit it.
      </span>
      <div className="grid grid-flow-col gap-x-12 auto-cols-max font-bold">
        <Link
          className="text-blue-500 font-bold"
          to={`/${subjectId}/assignments?status=unassigned&type=teacher-authored`}
        >
          All Resources
        </Link>
        <Link
          className="text-blue-500 font-bold"
          to={`/${subjectId}/question_bank/create?quizId=${itemId}`}
        >
          Edit Quiz
        </Link>
      </div>
    </div>
  );
  return toast(<Message />, { actionLink: true, timeout: 10 });
};

export const hasSubjectAccess = ({
  subjectId,
  isTeacher,
  isAdmin,
  isCoordinator,
  teacherSubjects,
  adminSubjects,
}) => {
  const isTeacherSubject =
    isTeacher && teacherSubjects.some((subject) => subject.id === subjectId);
  const isAdminOrCoordSubject =
    (isAdmin || isCoordinator) &&
    adminSubjects.some(
      ({ masterSubjectId }) => String(masterSubjectId) === subjectId,
    );
  return isTeacherSubject || isAdminOrCoordSubject;
};
